const React = require('react');
const PropTypes = require('prop-types');
const { filterAddonResources } = require('../../caroline/addons/portugueseStreams');
const { useParams, useLocation, useNavigate } = require('react-router');
const { useTranslation } = require('react-i18next');
const { useCore } = require('stremio/core');
const { useCoreSuspender } = require('stremio/common');
const { Button } = require('stremio/components');
const { playerPath, trailerIdFromVideoId, standaloneStreamFromPlayerLink } = require('../../caroline/playback/playerRoutes');
const { subscribeSelection, getPlayerSelection, preparePlayerNavigation, rememberSource, findRememberedSource } = require('../../caroline/playback/playerSelection');
const isSubscriptionStream = require('../../caroline/playback/isSubscriptionStream');
const { createManualSources } = require('../../caroline/playback/manualSources');
const { VERIFICATION_WINDOW_MS } = require('../../caroline/playback/verification');
const SourcesMenu = require('./SourcesMenu/SourcesMenu');
const styles = require('./styles');

// Reuse the exact existing MetaDetails requests. Never guess a stream or request
// playback resources until a remembered or explicitly chosen source exists.
const ResolveSelection = ({ identity }) => {
    const core = useCore();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const path = playerPath(identity);
    const [state, setState] = React.useState({ path, choices: [], loading: true });
    React.useEffect(() => {
        let active = true;
        let revision = 0;
        let expired = false;
        let restoring = false;
        const update = async (models) => {
            if (!models.includes('meta_details')) return;
            const current = ++revision;
            try {
                const model = await core.transport.getState('meta_details');
                if (!active || current !== revision || model.selected?.metaPath?.type !== identity.type ||
                    model.selected?.metaPath?.id !== identity.id || model.selected?.streamPath?.type !== identity.type ||
                    model.selected?.streamPath?.id !== identity.videoId) return;
                const streams = filterAddonResources(model.streams).filter((item) => item.content.type === 'Ready').flatMap((item) => item.content.content)
                    .filter((stream) => !isSubscriptionStream(stream));
                if (!restoring) {
                    const remembered = await findRememberedSource(path, streams, (stream) => core.transport.decodeStream(stream));
                    if (!active || current !== revision) return;
                    if (remembered) {
                        restoring = true;
                        preparePlayerNavigation(remembered);
                        return;
                    }
                }
                const loading = !expired && (model.streams.some((item) => item.content.type === 'Loading') ||
                    model.metaItem?.content?.type === 'Loading');
                setState({ path, choices: createManualSources(streams.map((stream) => ({ stream, state: 'unknown' }))), loading });
            } catch (_) {
                if (active && current === revision) setState({ path, choices: [], loading: false });
            }
        };
        core.on('state', update);
        core.transport.dispatch({ action: 'Load', args: { model: 'MetaDetails', args: {
            metaPath: { resource: 'meta', type: identity.type, id: identity.id, extra: [] },
            streamPath: { resource: 'stream', type: identity.type, id: identity.videoId, extra: [] }, guessStream: false,
        } } }, 'meta_details').then(() => update(['meta_details']), () => {
            if (active) setState({ path, choices: [], loading: false });
        });
        update(['meta_details']);
        const timer = setTimeout(() => {
            expired = true;
            setState((previous) => ({ path, choices: previous.path === path ? previous.choices : [], loading: false }));
            update(['meta_details']);
        }, VERIFICATION_WINDOW_MS);
        return () => {
            active = false;
            clearTimeout(timer);
            core.off('state', update);
            core.transport.dispatch({ action: 'Unload' }, 'meta_details');
        };
    }, [core, path, identity.type, identity.id, identity.videoId]);
    const detailsPath = `/metadetails/${[identity.type, identity.id, identity.videoId].map(encodeURIComponent).join('/')}`;
    const current = state.path === path ? state : { choices: [], loading: true };
    return <div className={styles['player-container']} style={{ overflow: 'auto', display: 'grid', placeContent: 'center' }}>
        <SourcesMenu type={identity.type} metaId={identity.id} videoId={identity.videoId} currentPath={path}
            initialChoices={current.choices} initialLoading={current.loading}
            onSelect={(_path, _identity, launchLink) => preparePlayerNavigation(launchLink)}
            onClose={() => navigate(detailsPath, { replace: true })} />
        <Button href={`#${detailsPath}`}>{t('MORE_INFO', { defaultValue: 'Detalhes' })}</Button>
    </div>;
};
ResolveSelection.propTypes = { identity: PropTypes.shape({ type: PropTypes.string, id: PropTypes.string, videoId: PropTypes.string }).isRequired };

const TrailerPlayer = ({ component: Component, identity, ytId }) => {
    const core = useCore();
    const { t } = useTranslation();
    const [state, setState] = React.useState({ status: 'loading', stream: null });

    React.useEffect(() => {
        let active = true;
        let settled = false;
        let revision = 0;
        const update = async (models) => {
            if (settled || !models.includes('meta_details')) return;
            const current = ++revision;
            try {
                const model = await core.transport.getState('meta_details');
                if (!active || current !== revision || model.selected?.metaPath?.type !== identity.type ||
                    model.selected?.metaPath?.id !== identity.id) return;

                const metaItem = model.metaItem?.content;
                if (metaItem?.type === 'Loading' || !metaItem) return;
                if (metaItem.type === 'Err') {
                    settled = true;
                    setState({ status: 'error', stream: null });
                    return;
                }

                const trailer = metaItem.content?.trailerStreams?.find((item) => item.ytId === ytId);
                const stream = standaloneStreamFromPlayerLink(trailer?.deepLinks?.player);
                settled = true;
                setState(stream
                    ? { status: 'ready', stream }
                    : { status: 'error', stream: null });
            } catch (_) {
                if (active && current === revision) {
                    settled = true;
                    setState({ status: 'error', stream: null });
                }
            }
        };

        core.on('state', update);
        core.transport.dispatch({
            action: 'Load',
            args: {
                model: 'MetaDetails',
                args: {
                    metaPath: { resource: 'meta', type: identity.type, id: identity.id, extra: [] },
                    streamPath: null,
                    guessStream: false,
                },
            },
        }, 'meta_details').then(() => update(['meta_details']), () => {
            if (active && !settled) {
                settled = true;
                setState({ status: 'error', stream: null });
            }
        });
        update(['meta_details']);
        const timeout = window.setTimeout(() => {
            setState((previous) => previous.status === 'loading' ? { status: 'error', stream: null } : previous);
        }, 15000);

        return () => {
            active = false;
            clearTimeout(timeout);
            core.off('state', update);
            core.transport.dispatch({ action: 'Unload' }, 'meta_details');
        };
    }, [core, identity.type, identity.id, ytId]);

    if (state.status === 'ready') {
        return <Component urlParams={{ stream: state.stream }}
            recoveryDetailsHref={`#/metadetails/${[identity.type, identity.id].map(encodeURIComponent).join('/')}`}
            recoveryTrailerUrl={`https://www.youtube.com/watch?v=${encodeURIComponent(ytId)}`} />;
    }

    const detailsPath = `#/metadetails/${[identity.type, identity.id].map(encodeURIComponent).join('/')}`;
    const message = state.status === 'loading'
        ? t('CAROLINE_TRAILER_LOADING', { defaultValue: 'A carregar trailer…' })
        : t('CAROLINE_TRAILER_UNAVAILABLE', { defaultValue: 'Não foi possível carregar este trailer. Tenta novamente mais tarde.' });

    return (
        <div
            className={styles['player-container']}
            role={state.status === 'loading' ? 'status' : 'alert'}
            style={{ display: 'grid', placeContent: 'center', justifyItems: 'center', gap: '1rem', color: 'white', textAlign: 'center' }}
        >
            <div>{message}</div>
            {state.status === 'error' ? <Button href={detailsPath}>{t('MORE_INFO', { defaultValue: 'Detalhes' })}</Button> : null}
            {state.status === 'error' ? <>
                <Button onClick={() => window.location.reload()}>{t('CAROLINE_RETRY', { defaultValue: 'Tentar novamente' })}</Button>
                <Button href={`https://www.youtube.com/watch?v=${encodeURIComponent(ytId)}`} target={'_blank'}>
                    {t('CAROLINE_TRAILER_EXTERNAL', { defaultValue: 'Ver trailer no YouTube' })}
                </Button>
            </> : null}
        </div>
    );
};
TrailerPlayer.propTypes = {
    component: PropTypes.elementType.isRequired,
    identity: PropTypes.shape({ type: PropTypes.string, id: PropTypes.string }).isRequired,
    ytId: PropTypes.string.isRequired,
};

const SelectedPlayer = ({ component: Component, selection }) => {
    const { decodeStream } = useCoreSuspender();
    const stream = decodeStream(selection.stream);
    React.useEffect(() => { rememberSource(selection, stream); }, [selection, stream]);
    return <Component urlParams={selection} />;
};
SelectedPlayer.propTypes = { component: PropTypes.elementType.isRequired, selection: PropTypes.object.isRequired };

const PlayerRoute = ({ component }) => {
    const params = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const identity = { type: params.mediaType || params.type, id: params.metaId || params.id, videoId: params.videoId };
    const path = playerPath(identity);
    const trailerId = trailerIdFromVideoId(identity.videoId);
    const getSnapshot = React.useCallback(() => getPlayerSelection(path), [path]);
    const selection = React.useSyncExternalStore(subscribeSelection, getSnapshot, getSnapshot);
    React.useEffect(() => {
        if (params.stream && path) {
            const target = preparePlayerNavigation(location.pathname + location.search);
            navigate(target, { replace: true });
        }
    }, [params.stream, path, location.pathname, location.search, navigate]);
    // A legacy full URL is replaced before mounting playback. Standalone Core
    // trailer links have no media identity and keep the existing decode path.
    if (params.stream && path) return <div className={styles['player-container']} />;
    if (path && trailerId) return <TrailerPlayer component={component} identity={identity} ytId={trailerId} />;
    if (!path) return <SelectedPlayer component={component} selection={params} />;
    return selection ? <SelectedPlayer component={component} selection={selection} /> : <ResolveSelection identity={identity} />;
};
PlayerRoute.propTypes = { component: PropTypes.elementType.isRequired };
module.exports = PlayerRoute;
